import { Card, Col, Container, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { copyToClipboard } from 'utils';

// ============= CONSTANTS =============
const CIRCLE_DEGREE_MULTIPLIER = 3.6;

interface StudentMetricsDisplayProps {
    averageMetrics: {
        activePercent: number;
        completedPercent: number;
        startedPercent: number;
        studentProgress: number;
        timeSpent: number;
    };
    calculateHourMinFormat: (minutes: number) => string;
}

export interface AverageMetrics {
    activePercent: number;
    completedPercent: number;
    startedPercent: number;
    studentProgress: number;
    timeSpent: number;
}

export const ReportToolTip = ({ id, children, copyText, href, tooltip, trigger = 'click' }: any) => {

    const normalizedTrigger = trigger === 'hover' ? ['hover', 'focus'] : trigger;
    return (
        <OverlayTrigger
            rootClose
            overlay={(
                <Tooltip id={id}>
                    {tooltip}
                </Tooltip>
            )}
            popperConfig={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 5]
                        }
                    },
                    {
                        name: 'preventOverflow',
                        options: {
                            padding: 8
                        }
                    }
                ]
            }}
            container={typeof document !== 'undefined' ? document.body : undefined}
            placement="top"
            trigger={normalizedTrigger}
        >
            <span
                onClick={() => {

                    if (copyText) {

                        copyToClipboard(copyText);

                    }

                }}
                style={{
                    cursor: 'pointer',
                    display: 'inline-block'
                }}
            >
                {children}
            </span>
        </OverlayTrigger>
    );

};

export const Circle = ({ label, value }: any) => (
    <div className="text-center my-3">
        <div className="mb-2 fw-semibold text-secondary">
            {label}
        </div>
        <div
            style={{
                background: `conic-gradient(#28a745 ${value * CIRCLE_DEGREE_MULTIPLIER}deg, #e9ecef 0deg)`,
                borderRadius: '50%',
                height: 100,
                width: 100
            }}
            className="position-relative d-inline-flex justify-content-center align-items-center"
        >
            <div
                className="position-absolute bg-white rounded-circle d-flex justify-content-center align-items-center"
                style={{ height: 90, width: 90 }}
            >
                <span className="fw-bold fs-4">
                    {Math.round(value)}
                    %
                </span>
            </div>
        </div>
    </div>
);

export const StudentMetricsDisplay: React.FC<StudentMetricsDisplayProps> = ({
    averageMetrics,
    calculateHourMinFormat
}) => {

    const { formatMessage } = useIntl();

    return ((
        <Container fluid className="px-0 mb-4">
            <Row className="g-3 align-items-stretch mx-0">
                {/* Student Distribution */}
                <Col className="ps-0" md="7" xs="12">
                    <Card className="p-3 shadow-sm h-100">
                        <h6 className="fw-semibold mb-3">
                            <FormattedMessage defaultMessage="Student Distribution" id="student_distribution.4311af2f" />
                        </h6>
                        <Row className="text-center">
                            <Col md="4" xs="12">
                                <ReportToolTip id="started-percent-tooltip" tooltip={formatMessage({ id: 'percent_of_enrolled_students_who_have_st.54311c37', defaultMessage: 'Percent of enrolled students who have started at least one resource' })} trigger="hover">
                                    <Circle label={formatMessage({ id: 'started.d612a775', defaultMessage: 'Started' })} value={averageMetrics.startedPercent} />
                                </ReportToolTip>
                            </Col>
                            <Col md="4" xs="12">
                                <ReportToolTip id="completed-percent-tooltip" tooltip={formatMessage({ id: 'percent_of_enrolled_students_who_have_co.3fce926b', defaultMessage: 'Percent of enrolled students who have completed all required resources' })} trigger="hover">
                                    <Circle label={formatMessage({ id: 'completed.cd3d7862', defaultMessage: 'Completed' })} value={averageMetrics.completedPercent} />
                                </ReportToolTip>
                            </Col>
                            <Col md="4" xs="12">
                                <ReportToolTip tooltip={formatMessage({ id: 'percent_of_students_who_engaged_with_the.834b54e9', defaultMessage: 'Percent of students who engaged with the course within the past 7 days' })} trigger="hover">
                                    <Circle label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })} value={averageMetrics.activePercent} />
                                </ReportToolTip>
                            </Col>
                        </Row>
                    </Card>
                </Col>

                {/* Student Averages */}
                <Col className="pe-0" md="5" xs="12">
                    <Card className="p-3 shadow-sm h-100">
                        <h6 className="fw-semibold mb-3">
                            <FormattedMessage defaultMessage="Student Averages" id="student_averages.6380fee9" />
                        </h6>
                        <Row className="text-center align-items-center">
                            <Col md="6" xs="12">
                                <ReportToolTip tooltip={formatMessage({ id: 'average_percent_complete_across_all_enro.07fb6a28', defaultMessage: 'Average percent complete across all enrolled students' })} trigger="hover">
                                    <Circle label={formatMessage({ id: 'progress.2797cee5', defaultMessage: 'Progress' })} value={averageMetrics.studentProgress} />
                                </ReportToolTip>
                            </Col>
                            <Col md="6" xs="12">
                                <ReportToolTip tooltip={formatMessage({ id: 'average_time_spent_per_student_across_al.747427dc', defaultMessage: 'Average time spent per student across all resources' })} trigger="hover">
                                    <div className="text-center my-3">
                                        <div className="fw-semibold text-secondary mb-2">
                                            <FormattedMessage defaultMessage="Time Spent" id="time_spent.da775c0c" />
                                        </div>
                                        <div className="fw-bold fs-3">
                                            {calculateHourMinFormat(Math.round(averageMetrics.timeSpent))}
                                        </div>
                                    </div>
                                </ReportToolTip>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
        </Container>
    ));

};
